import React, { useEffect, useState } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Select,
  MenuItem,
  Typography,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useLocation } from 'react-router-dom';
import { track } from '../../../utils/track';

import {
  SettingsUserDetails,
  SettingsSecurity,
  SettingsUserSubscription,
  SettingsUserReferrals,
  SettingsUserPayments,
  SettingsUserProfileSettings,
  SettingsTAndC
} from './components';

const MONO = 'ui-monospace, "SF Mono", Menlo, Consolas, monospace';

// One dark (arena) theme applied to the whole Settings surface — every control
// across every tab (TextField, Select, Button, Radio, etc.) renders dark,
// cyan-focused and rounded instead of the app's default light theme, so we
// don't have to restyle each tab component by hand.
const settingsTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#38e8ff' },
    error: { main: '#ff6b6b' },
    background: { paper: '#0b1119', default: '#070b11' },
    text: { primary: '#e8f4fb', secondary: '#8ea3b3' }
  },
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: 'rgba(7, 11, 17, 0.55)',
          '& fieldset': { borderColor: 'rgba(56, 232, 255, 0.28)' },
          '&:hover fieldset': { borderColor: 'rgba(56, 232, 255, 0.5)' },
          '&.Mui-focused fieldset': { borderColor: '#38e8ff' }
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { color: '#8ea3b3', '&.Mui-focused': { color: '#38e8ff' } }
      }
    },
    MuiFormHelperText: {
      styleOverrides: { root: { fontFamily: MONO, fontSize: '0.72rem' } }
    }
  }
});

// Arena panel chrome (dark gradient + cyan hairline) reused by the tab card and
// the profile card.
const panelSx = {
  borderRadius: '16px',
  border: '1px solid rgba(56, 232, 255, 0.22)',
  background:
    'linear-gradient(180deg, rgba(11, 17, 25, 0.74), rgba(7, 11, 17, 0.78))',
  boxShadow: '0 18px 40px -26px rgba(0, 0, 0, 0.9)',
  overflow: 'hidden'
} as const;

export const UserSettings = () => {
  const { search } = useLocation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  // ?tab=security opens Security — on phones too, where the profile card otherwise
  // takes over. The recovery-code sign-in notice links here.
  const securityLink = new URLSearchParams(search).get('tab') === 'security';
  const getInitialTab = () =>
    securityLink ? 1 : search === '?referral=true' ? 3 : 0;
  const [selectedTab, setSelectedTab] = useState<number>(getInitialTab());

  useEffect(() => {
    if (securityLink) return setSelectedTab(1);
    if (isSmallScreen) return setSelectedTab(-1);
  }, [isSmallScreen, securityLink]);

  return (
    <Box sx={{ p: { xs: '1.25rem', md: '2rem' } }}>
      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{
            font: `800 10px ${MONO}`,
            letterSpacing: '0.4em',
            textTransform: 'uppercase',
            color: '#ffcf40'
          }}
        >
          Settings
        </Typography>
        <Typography
          sx={{
            mt: 1.25,
            fontWeight: 900,
            fontSize: 'clamp(24px, 3.4vw, 36px)',
            lineHeight: 1,
            letterSpacing: '-0.02em',
            color: '#e8f4fb'
          }}
        >
          Your{' '}
          <Box component="span" sx={{ color: '#38e8ff' }}>
            account
          </Box>
        </Typography>
      </Box>

      <ThemeProvider theme={settingsTheme}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'flex-start',
            gap: 3
          }}
        >
          {!isSmallScreen && (
            <Box sx={{ width: '30%', minWidth: 260 }}>
              <SettingsUserProfileSettings />
            </Box>
          )}

          <Box sx={{ width: isSmallScreen ? '100%' : '70%', flex: 1 }}>
            <Box sx={panelSx}>
              {isSmallScreen ? (
                <Box sx={{ p: 2, pb: 0 }}>
                  <Select
                    value={selectedTab}
                    onChange={(e) => {
                      const t = e.target.value as number;
                      setSelectedTab(t);
                      track('settings.section', { tab: t });
                    }}
                    fullWidth
                  >
                    <MenuItem value={-1}>Profile Settings</MenuItem>
                    <MenuItem value={0}>User Details</MenuItem>
                    <MenuItem value={1}>Security</MenuItem>
                    <MenuItem value={2}>Referrals</MenuItem>
                    <MenuItem value={3}>Subscription</MenuItem>
                    <MenuItem value={4}>Payments</MenuItem>
                    <MenuItem value={5}>T&Cs</MenuItem>
                  </Select>
                </Box>
              ) : (
                <Tabs
                  value={selectedTab}
                  onChange={(_, tabIndex) => {
                    setSelectedTab(tabIndex);
                    track('settings.section', { tab: tabIndex });
                  }}
                  variant="scrollable"
                  orientation="horizontal"
                  textColor="inherit"
                  sx={{
                    px: 1,
                    borderBottom: '1px solid rgba(56, 232, 255, 0.15)',
                    '& .MuiTab-root': {
                      color: '#8ea3b3',
                      fontWeight: 800,
                      letterSpacing: '0.06em'
                    },
                    '& .Mui-selected': { color: '#38e8ff' },
                    '& .MuiTabs-indicator': { backgroundColor: '#38e8ff' }
                  }}
                >
                  <Tab label="User Details" />
                  <Tab label="Security" />
                  <Tab label="Referrals" />
                  <Tab label="Subscription" />
                  <Tab label="Payments" />
                  <Tab label="T&Cs" />
                </Tabs>
              )}

              <Box sx={{ p: { xs: 2, md: 3 } }}>
                {isSmallScreen && selectedTab === -1 && (
                  <SettingsUserProfileSettings />
                )}
                {selectedTab === 0 && <SettingsUserDetails />}
                {selectedTab === 1 && <SettingsSecurity />}
                {selectedTab === 2 && (
                  <SettingsUserReferrals isSmallSize={isSmallScreen} />
                )}
                {selectedTab === 3 && <SettingsUserSubscription />}
                {selectedTab === 4 && <SettingsUserPayments />}
                {selectedTab === 5 && <SettingsTAndC />}
              </Box>
            </Box>
          </Box>
        </Box>
      </ThemeProvider>
    </Box>
  );
};
